import type { CommunityGroupV2Dto, SelfProjectDto } from "@/api/types";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useSlug } from "@/hooks/useSlug";
import { OnboardingFlowCard } from "@/modules/onboarding-flow/components/OnboardingFlowCard";
import { OnboardingFlowActions } from "@/modules/onboarding-flow/components/OnboardingFlowLayout";
import { OnboardingGroup } from "@/modules/onboarding-flow/components/OnboardingGroup";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface OnboardingHelpCategoriesProps {
  helpCategories: CommunityGroupV2Dto[];
  project: SelfProjectDto;
  onFollow: (payload: { id: string; groupType: CommunityGroupV2Dto["type"] }) => void;
  onUnfollow: (payload: { id: string; groupType: CommunityGroupV2Dto["type"] }) => void;
  onMarkOnboarded: () => void;
  isMarkingAsOnboarded: boolean;
}
export function OnboardingHelpCategories({
  helpCategories,
  project,
  onFollow,
  onUnfollow,
  onMarkOnboarded,
  isMarkingAsOnboarded,
}: OnboardingHelpCategoriesProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();

  return (
    <OnboardingFlowCard>
      <Stack gap="sm">
        <h3 className="text-heading-3">{t("page.onboarding.help-categories.title")}</h3>
        <span className="text-body">{t("page.onboarding.help-categories.description")}</span>
        <span className="text-caption-emphasis text-text-secondary">
          {t("page.onboarding.help-categories.subtext")}
        </span>
      </Stack>
      <Stack gap="sm">
        {helpCategories.map((helpCategory) => (
          <OnboardingGroup
            key={helpCategory.id}
            group={helpCategory}
            projectDefaultImage={project.backgroundImage}
            onFollow={() => onFollow({ id: helpCategory.id, groupType: helpCategory.type })}
            onUnfollow={() => onUnfollow({ id: helpCategory.id, groupType: helpCategory.type })}
          />
        ))}
      </Stack>
      <OnboardingFlowActions>
        <Stack gap="sm">
          <Button fullWidth data-testid="next-step-button" isLoading={isMarkingAsOnboarded} onClick={onMarkOnboarded}>
            {t("common.action.next")}
          </Button>
          <Button
            type="link"
            variant="soft"
            href={routes.onboardingFlow.groups({
              slug,
            })}
            fullWidth
          >
            {t("common.action.back")}
          </Button>
        </Stack>
      </OnboardingFlowActions>
    </OnboardingFlowCard>
  );
}
